import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { datadogRum } from '@datadog/browser-rum';

import { sessionAccountNumberKey } from '@/context/AccountNumberContext/withFetch/sessionKey';
import { useAddJourneyCompletionReward } from '@/hooks/utils/useAddJourneyCompletionReward';
import {
  RewardJourneyType,
  RewardJourneyParams,
  REWARD_JOURNEY_SESSION_KEY,
} from '@/types/rewards';

export const useRewardJourney = () => {
  const router = useRouter();

  const { mutate: addJourneyCompletionReward } =
    useAddJourneyCompletionReward();

  const setRewardJourneyCompleted = useCallback(
    (journey: RewardJourneyType) => {
      // Store in session storage for security
      const journeyData: RewardJourneyParams = {
        journey,
        timestamp: new Date().toISOString(),
      };
      sessionStorage.setItem(
        REWARD_JOURNEY_SESSION_KEY,
        JSON.stringify(journeyData)
      );

      // Track in Datadog
      datadogRum.addAction('reward_journey_completed', {
        journey,
        timestamp: journeyData.timestamp,
      });
    },
    []
  );

  const getRewardJourneyCompleted =
    useCallback((): RewardJourneyParams | null => {
      try {
        const stored = sessionStorage.getItem(REWARD_JOURNEY_SESSION_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    }, []);

  const clearRewardJourneyCompleted = useCallback(() => {
    sessionStorage.removeItem(REWARD_JOURNEY_SESSION_KEY);
    sessionStorage.removeItem(sessionAccountNumberKey);
  }, []);

  const redirectToRewards = useCallback(
    (journey: RewardJourneyType) => {
      setRewardJourneyCompleted(journey);

      // Add query parameter for tracking
      const query = { journey };
      router.push({
        pathname: '/rewards',
        query,
      });
    },
    [router, setRewardJourneyCompleted]
  );

  const validateRewardAccess = useCallback((): boolean => {
    const journeyData = getRewardJourneyCompleted();
    const queryJourney = router.query.journey as RewardJourneyType;

    // Must have session storage data
    if (!journeyData) {
      return false;
    }

    // Query parameter must match session storage (if present)
    if (queryJourney && journeyData.journey !== queryJourney) {
      return false;
    }

    // Journey type must be valid
    return Object.values(RewardJourneyType).includes(journeyData.journey);
  }, [router.query.journey, getRewardJourneyCompleted]);

  const validateInitialRewardAccess = useCallback((): boolean => {
    const journeyData = getRewardJourneyCompleted();
    const queryJourney = router.query.journey as RewardJourneyType;

    // Must have session storage data
    if (!journeyData) {
      return false;
    }

    // Must have query parameter for initial access
    if (!queryJourney) {
      return false;
    }

    // Query parameter must match session storage
    if (journeyData.journey !== queryJourney) {
      return false;
    }

    // Journey type must be valid
    return Object.values(RewardJourneyType).includes(journeyData.journey);
  }, [router.query.journey, getRewardJourneyCompleted]);

  const addReward = useCallback(
    (journey: RewardJourneyType) => {
      const accountNumber = sessionStorage.getItem(sessionAccountNumberKey);

      // Use mutation to save reward
      if (accountNumber) {
        addJourneyCompletionReward({
          input: {
            accountNumber,
            journey,
          },
        });
      }
    },
    [addJourneyCompletionReward]
  );

  return {
    setRewardJourneyCompleted,
    getRewardJourneyCompleted,
    clearRewardJourneyCompleted,
    redirectToRewards,
    validateRewardAccess,
    validateInitialRewardAccess,
    addReward,
  };
};
